import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { api, useAuthStore } from '@/store';
import { useNavigate } from 'react-router-dom';
import {
  Blocks, Plug, Loader2, CheckCircle2, Trash2, ExternalLink, Zap, Image as ImageIcon, Users, RefreshCw,
} from 'lucide-react';

// Ferramentas externas suportadas (não são painéis IPTV)
const EXTERNAL_TOOLS = [
  {
    provider: 'geradorpro',
    name: 'Gerador Pro',
    icon: '🎨',
    color: 'from-fuchsia-500 to-purple-600',
    description: 'Gerador de banners. Importe, renove e gerencie seus clientes do Gerador Pro pelo NexuZap.',
    url: 'https://gerador.pro',
    urlLabel: 'gerador.pro',
  },
  {
    provider: 'fshorts',
    name: 'FShorts',
    icon: '🎬',
    color: 'from-cyan-500 to-blue-600',
    description: 'Plataforma de reels/shorts. Importe, renove e gerencie seus clientes do FShorts pelo NexuZap.',
    url: 'https://reels.gtdown.com',
    urlLabel: 'reels.gtdown.com',
  },
  {
    provider: 'gerenciaapp',
    name: 'GerenciaApp',
    icon: '📺',
    color: 'from-emerald-500 to-teal-600',
    description: 'Gerencie e renove os masters e revendas do GerenciaApp (seus clientes) automaticamente pelo NexuZap.',
    url: 'https://gerenciaapp.top',
    urlLabel: 'gerenciaapp.top',
    loginEmail: true,
  },
];

export default function ExternalsPage() {
  const [loading, setLoading] = useState(true);
  const [integrations, setIntegrations] = useState([]);
  const [connectOpen, setConnectOpen] = useState(null); // tool sendo conectada
  const [form, setForm] = useState({ username: '', password: '', name: '' });
  const [saving, setSaving] = useState(false);
  const [plansOpen, setPlansOpen] = useState(null);
  const [syncing, setSyncing] = useState(null);
  const [migrando, setMigrando] = useState(null); // integração para gerenciar planos
  const [plans, setPlans] = useState([]);
  const [newPlan, setNewPlan] = useState({ name: '', months: 1, price: '' });
  const [importOpen, setImportOpen] = useState(null); // integração
  const [importPlans, setImportPlans] = useState([]);
  const [importPlanId, setImportPlanId] = useState('');
  const [pulling, setPulling] = useState(false);
  const [pulled, setPulled] = useState([]); // clientes do painel
  const [selected, setSelected] = useState({});
  const [importing, setImporting] = useState(false);
  const [billOffer, setBillOffer] = useState(null); // { customers: [...] }
  const [autoOpen, setAutoOpen] = useState(null); // integração
  const [autoCfg, setAutoCfg] = useState({ enabled: false, plan_id: '', connection_id: '', auto_billing: false });
  const [autoPlans, setAutoPlans] = useState([]);
  const [savingAuto, setSavingAuto] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const isSimple = user?.role === 'simple';
  const [connections, setConnections] = useState([]);
  const [connectionId, setConnectionId] = useState('');

  const load = async () => {
    try {
      const r = await api.get('/sigma/login/integrations?scope=external');
      const all = r.data.integrations || r.data || [];
      setIntegrations(all.filter((i) => EXTERNAL_TOOLS.some((t) => t.provider === i.provider)));
    } catch { setIntegrations([]); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const connectedOf = (provider) => integrations.find((i) => i.provider === provider);

  const openPlans = async (integ) => {
    setPlansOpen(integ);
    setNewPlan({ name: '', months: 1, price: '' });
    try {
      const r = await api.get('/customer-plans');
      const all = r.data.plans || r.data || [];
      setPlans(all.filter((p) => p.sigma_login_integration_id === integ.id));
    } catch { setPlans([]); }
  };
  const openAuto = async (integ) => {
    setAutoOpen(integ);
    try {
      const [rc, rp, rcon] = await Promise.all([
        api.get(`/sigma/login/${integ.id}/auto-import`),
        api.get('/customer-plans'),
        (!isSimple ? api.get('/connections/quick?owner_filter=mine') : Promise.resolve({ data: [] })),
      ]);
      const cfg = rc.data || {};
      setAutoCfg({ enabled: !!cfg.enabled, plan_id: cfg.plan_id || '', connection_id: cfg.connection_id || '', auto_billing: !!cfg.auto_billing });
      const all = rp.data.plans || rp.data || [];
      setAutoPlans(all.filter((p) => p.sigma_login_integration_id === integ.id));
      setConnections(rcon.data || []);
    } catch { /* ignore */ }
  };
  const saveAuto = async () => {
    if (autoCfg.enabled && !autoCfg.plan_id) { toast.error('Escolha um plano'); return; }
    setSavingAuto(true);
    try {
      // Nexu Simple não tem WhatsApp: nunca envia conexão nem cobrança automática
      const payload = isSimple
        ? { enabled: autoCfg.enabled, plan_id: autoCfg.plan_id, connection_id: null, auto_billing: false }
        : autoCfg;
      await api.put(`/sigma/login/${autoOpen.id}/auto-import`, payload);
      toast.success('Automação salva!');
      setAutoOpen(null);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao salvar'); }
    finally { setSavingAuto(false); }
  };

  const openImportAll = async (integ) => {
    setImportOpen(integ); setPulled([]); setSelected({}); setImportPlanId(''); setConnectionId('');
    try {
      const r = await api.get('/customer-plans');
      const all = r.data.plans || r.data || [];
      const list = all.filter((p) => p.sigma_login_integration_id === integ.id);
      setImportPlans(list);
      if (list[0]) setImportPlanId(list[0].id);
    } catch { setImportPlans([]); }
    // conexões (para vincular ao WhatsApp) — Nexu Simple não tem
    if (!isSimple) {
      try {
        const rc = await api.get('/connections/quick?owner_filter=mine');
        const conns = rc.data || [];
        setConnections(conns);
        if (conns.length === 1) setConnectionId(conns[0].id); // 1 conexão: já vincula
      } catch { setConnections([]); }
    } else { setConnections([]); }
  };
  const pullAll = async () => {
    setPulling(true);
    try {
      const r = await api.post(`/sigma/login/${importOpen.id}/search-customers`, { query: '', only_mine: true });
      const list = r.data.customers || [];
      setPulled(list);
      // pré-seleciona os que ainda não foram importados
      const sel = {};
      list.forEach((c) => { if (!c.already_imported) sel[c.panel_customer_id] = true; });
      setSelected(sel);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao puxar clientes'); }
    finally { setPulling(false); }
  };
  const importSelected = async () => {
    if (!importPlanId) { toast.error('Escolha um plano'); return; }
    const ids = pulled.filter((c) => selected[c.panel_customer_id] && !c.already_imported).map((c) => c.panel_customer_id);
    if (!ids.length) { toast.error('Selecione ao menos um cliente novo'); return; }
    setImporting(true);
    try {
      const r = await api.post(`/sigma/login/${importOpen.id}/${importOpen.provider === 'fshorts' ? 'fshorts' : 'geradorpro'}-import`, {
        panel_customer_ids: ids, plan_id: importPlanId,
        connection_id: connectionId || null,
      });
      toast.success(r.data.message || 'Importado!');
      setImportOpen(null); load();
      const billable = r.data.billable || [];
      if (billable.length > 0) setBillOffer({ customers: billable });
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao importar'); }
    finally { setImporting(false); }
  };

  const sendBillingToImported = async () => {
    const ids = (billOffer?.customers || []).map((c) => c.id);
    if (!ids.length) { setBillOffer(null); return; }
    try {
      await api.post('/customers/bulk-billing', { customer_ids: ids, delay_min: 3, delay_max: 6, kind: 'billing' });
      toast.success('Cobrança iniciada! Acompanhe pela bolinha no canto.');
      window.dispatchEvent(new CustomEvent('bulk-billing-started'));
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao iniciar cobrança'); }
    setBillOffer(null);
  };

  const createPlan = async () => {
    const price = parseFloat(newPlan.price);
    if (!newPlan.name.trim() || isNaN(price) || price < 0) { toast.error('Preencha nome e preço'); return; }
    try {
      await api.post('/customer-plans', {
        name: newPlan.name.trim(),
        price,
        duration_months: parseInt(newPlan.months) || 1,
        sigma_login_integration_id: plansOpen.id,
      });
      toast.success('Plano criado!');
      openPlans(plansOpen);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao criar plano'); }
  };
  const toggleShowInPayment = async (plan, val) => {
    try {
      await api.put(`/customer-plans/${plan.id}`, {
        name: plan.name, price: plan.price, duration_months: plan.duration_months,
        description: plan.description || '', active: plan.active !== false,
        sigma_package_id: plan.sigma_package_id || null,
        sigma_login_integration_id: plan.sigma_login_integration_id || null,
        sigma_login_package_id: plan.sigma_login_package_id || null,
        sigma_login_connections: plan.sigma_login_connections || null,
        show_in_payment: val,
      });
      setPlans((prev) => prev.map((x) => x.id === plan.id ? { ...x, show_in_payment: val } : x));
    } catch (e) { toast.error('Erro ao atualizar'); }
  };
  const deletePlan = async (id) => {
    if (!window.confirm('Excluir este plano?')) return;
    try { await api.delete(`/customer-plans/${id}`); toast.success('Plano excluído'); openPlans(plansOpen); }
    catch (e) { toast.error(e.response?.data?.detail || 'Não foi possível (plano em uso?)'); }
  };

  const openConnect = (tool) => {
    setForm({ username: '', password: '', name: tool.name });
    setConnectOpen(tool);
  };

  const doConnect = async () => {
    if (!form.username || !form.password) { toast.error('Preencha usuário e senha'); return; }
    setSaving(true);
    try {
      await api.post('/sigma/login/connect', {
        panel_url: connectOpen.url,
        username: form.username.trim(),
        password: form.password,
        name: form.name.trim() || connectOpen.name,
        provider: connectOpen.provider,
      });
      toast.success(`${connectOpen.name} conectado!`);
      setConnectOpen(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Falha ao conectar');
    } finally { setSaving(false); }
  };

  const migrarPlanos = async (integ) => {
    if (!window.confirm(`Migrar os planos antigos (token) para a integração ${integ.name}? Eles serão vinculados a este painel Sigma.`)) return;
    setMigrando(integ.id);
    try {
      const r = await api.post(`/sigma/login/${integ.id}/migrar-planos-token`);
      toast.success(r.data.mensagem || 'Planos migrados!');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao migrar'); }
    finally { setMigrando(null); }
  };
  const syncVincular = async (integ) => {
    setSyncing(integ.id);
    try {
      const r = await api.post(`/sigma/login/${integ.id}/sync-vincular`);
      toast.success(r.data.mensagem || 'Sincronizado!');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao sincronizar'); }
    finally { setSyncing(null); }
  };
  const disconnect = async (integ) => {
    if (!window.confirm(`Desconectar ${integ.name || 'esta conta'}? Os clientes já importados continuam no gestor, sem o vínculo.`)) return;
    try {
      await api.delete(`/sigma/login/integrations/${integ.id}`);
      toast.success('Desconectado');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao desconectar'); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-5 max-w-4xl mx-auto">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-2xl bg-primary/15 text-primary flex items-center justify-center">
          <Blocks className="w-6 h-6" />
        </div>
        <div>
          <h1 className="text-xl font-bold">Externos</h1>
          <p className="text-sm text-muted-foreground">Ferramentas externas (não são painéis IPTV) que se integram ao seu gestor.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {EXTERNAL_TOOLS.map((tool) => {
          const conn = connectedOf(tool.provider);
          return (
            <Card key={tool.provider} className="overflow-hidden">
              <CardContent className="p-5">
                <div className="flex items-start gap-3">
                  <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${tool.color} flex items-center justify-center text-2xl shrink-0`}>
                    {tool.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-bold">{tool.name}</h3>
                      {conn && <CheckCircle2 className="w-4 h-4 text-green-500" />}
                    </div>
                    <a href={tool.url} target="_blank" rel="noreferrer"
                       className="text-xs text-primary hover:underline inline-flex items-center gap-1">
                      {tool.urlLabel} <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground mt-3">{tool.description}</p>

                {conn ? (
                  <div className="mt-4 space-y-2">
                    <div className="rounded-xl border border-green-500/25 bg-green-500/5 px-3 py-2.5 flex items-center justify-between">
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{conn.name || conn.panel_username}</p>
                        <p className="text-xs text-green-500">Conectado como {conn.panel_username}</p>
                      </div>
                      <Button size="icon" variant="ghost" className="text-red-500 hover:text-red-400 shrink-0"
                        onClick={() => disconnect(conn)} title="Desconectar">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    <Button className="w-full" size="sm" onClick={() => openImportAll(conn)}>
                      <Zap className="w-4 h-4 mr-1.5" /> Puxar todos os clientes
                    </Button>
                    <Button variant="outline" className="w-full" size="sm" disabled={syncing === conn.id} onClick={() => syncVincular(conn)}>
                      {syncing === conn.id ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1.5" />}
                      Sincronizar e vincular
                    </Button>

                    <Button variant="outline" className="w-full" size="sm" onClick={() => openAuto(conn)}>
                      <Loader2 className="w-4 h-4 mr-1.5" /> Automação (importar sozinho)
                    </Button>
                    <div className="grid grid-cols-2 gap-2">
                      <Button variant="outline" size="sm" onClick={() => navigate(`/customers?integration=${conn.id}`)}>
                        <Users className="w-4 h-4 mr-1.5" /> Ver clientes
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => openPlans(conn)}>
                        <ImageIcon className="w-4 h-4 mr-1.5" /> Planos
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button className="w-full mt-4" onClick={() => openConnect(tool)}>
                    <Plug className="w-4 h-4 mr-2" /> Conectar {tool.name}
                  </Button>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Modal de automação */}
      <Dialog open={!!autoOpen} onOpenChange={(o) => { if (!o) setAutoOpen(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Zap className="w-5 h-5 text-primary" /> Importação automática</DialogTitle>
            <DialogDescription>O NexuZap importa sozinho os clientes novos do {autoOpen?.name} a cada 1 hora.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <label className="flex items-center justify-between rounded-lg border border-border/60 px-3 py-2.5 cursor-pointer">
              <span className="text-sm font-medium">Ativar importação automática</span>
              <input type="checkbox" checked={autoCfg.enabled}
                onChange={(e) => setAutoCfg({ ...autoCfg, enabled: e.target.checked })}
                className="w-4 h-4 accent-primary" />
            </label>

            {autoCfg.enabled && (
              <>
                <div>
                  <Label className="text-xs">Plano dos clientes importados</Label>
                  <Select value={autoCfg.plan_id} onValueChange={(v) => setAutoCfg({ ...autoCfg, plan_id: v })}>
                    <SelectTrigger><SelectValue placeholder="Escolha o plano" /></SelectTrigger>
                    <SelectContent>
                      {autoPlans.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} — {p.duration_months} {p.duration_months===1?'mês':'meses'}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {autoPlans.length === 0 && <p className="text-[11px] text-amber-500 mt-1">Crie um plano do Gerador Pro primeiro (botão Planos).</p>}
                </div>

                {!isSimple && connections.length > 0 && (
                  <div>
                    <Label className="text-xs">Conexão WhatsApp</Label>
                    <Select value={autoCfg.connection_id} onValueChange={(v) => setAutoCfg({ ...autoCfg, connection_id: v })}>
                      <SelectTrigger><SelectValue placeholder="Selecione (opcional)" /></SelectTrigger>
                      <SelectContent>
                        {connections.map((c) => <SelectItem key={c.id} value={c.id}>{c.name || c.phone || 'Conexão'}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {!isSimple && (
                  <label className="flex items-start gap-2 rounded-lg border border-amber-500/25 bg-amber-500/5 px-3 py-2.5 cursor-pointer">
                    <input type="checkbox" checked={autoCfg.auto_billing}
                      onChange={(e) => setAutoCfg({ ...autoCfg, auto_billing: e.target.checked })}
                      className="mt-0.5 w-4 h-4 accent-primary" disabled={!autoCfg.connection_id} />
                    <span className="text-xs">
                      Enviar cobrança automática
                      <span className="text-muted-foreground"> — para os importados que estiverem vencidos ou vencendo em breve. Precisa de uma conexão WhatsApp.</span>
                    </span>
                  </label>
                )}
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAutoOpen(null)}>Cancelar</Button>
            <Button onClick={saveAuto} disabled={savingAuto}>
              {savingAuto ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Oferta de cobrança pós-importação */}
      <Dialog open={!!billOffer} onOpenChange={(o) => { if (!o) setBillOffer(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Zap className="w-5 h-5 text-amber-500" /> Enviar cobrança?</DialogTitle>
            <DialogDescription>
              {(() => {
                const cs = billOffer?.customers || [];
                const vencidos = cs.filter((c) => c.days_left < 0).length;
                const vencendo = cs.length - vencidos;
                const partes = [];
                if (vencidos) partes.push(`${vencidos} vencido(s)`);
                if (vencendo) partes.push(`${vencendo} vencendo em breve`);
                return `Você importou ${partes.join(' e ')}. Quer enviar a mensagem de cobrança para eles agora?`;
              })()}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setBillOffer(null)}>Agora não</Button>
            <Button onClick={sendBillingToImported}>Sim, enviar cobrança</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal puxar todos os clientes */}
      <Dialog open={!!importOpen} onOpenChange={(o) => { if (!o) setImportOpen(null); }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Zap className="w-5 h-5 text-primary" /> Puxar clientes do {importOpen?.name}</DialogTitle>
            <DialogDescription>Traz todos os clientes do painel. Os já importados ficam marcados e não são importados de novo.</DialogDescription>
          </DialogHeader>

          <div className="flex items-center gap-2">
            <Select value={importPlanId} onValueChange={setImportPlanId}>
              <SelectTrigger className="flex-1"><SelectValue placeholder="Escolha o plano" /></SelectTrigger>
              <SelectContent>
                {importPlans.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} — {p.duration_months} {p.duration_months===1?'mês':'meses'}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button onClick={pullAll} disabled={pulling}>
              {pulling ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Puxar'}
            </Button>
          </div>
          {importPlans.length === 0 && <p className="text-xs text-amber-500 mt-2">Crie um plano do Gerador Pro primeiro (botão Planos).</p>}

          {!isSimple && connections.length > 0 && (
            <div className="mt-2">
              <Label className="text-xs">Conexão WhatsApp {connections.length === 1 ? '(vinculada automaticamente)' : '(escolha)'}</Label>
              <Select value={connectionId} onValueChange={setConnectionId}>
                <SelectTrigger><SelectValue placeholder="Selecione a conexão" /></SelectTrigger>
                <SelectContent>
                  {connections.map((c) => <SelectItem key={c.id} value={c.id}>{c.name || c.phone || 'Conexão'}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          {!isSimple && connections.length === 0 && (
            <p className="text-xs text-muted-foreground mt-2">Sem conexão WhatsApp — os clientes serão importados sem vínculo de conexão.</p>
          )}

          {pulled.length > 0 && (
            <>
              <div className="flex items-center justify-between mt-3 mb-1 text-xs text-muted-foreground">
                <span>{pulled.length} no painel · {pulled.filter(c=>c.already_imported).length} já importados</span>
                <div className="flex gap-2">
                  <button className="text-primary" onClick={() => { const s={}; pulled.forEach(c=>{if(!c.already_imported)s[c.panel_customer_id]=true;}); setSelected(s); }}>Todos novos</button>
                  <button className="text-muted-foreground" onClick={() => setSelected({})}>Limpar</button>
                </div>
              </div>
              <div className="max-h-[40vh] overflow-y-auto space-y-1.5">
                {pulled.map((c) => (
                  <label key={c.panel_customer_id}
                    className={`flex items-center gap-3 rounded-lg border p-2.5 ${c.already_imported ? 'opacity-50 border-border/40' : 'border-border/60 cursor-pointer hover:border-primary/40'}`}>
                    <input type="checkbox" disabled={c.already_imported}
                      checked={!!selected[c.panel_customer_id]}
                      onChange={(e) => setSelected({ ...selected, [c.panel_customer_id]: e.target.checked })}
                      className="w-4 h-4 accent-primary" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{c.username}</p>
                      <p className="text-xs text-muted-foreground">vence {(c.expiration_date||'').split('T')[0] || '—'}{c.whatsapp ? ` · ${c.whatsapp}` : ''}</p>
                    </div>
                    {c.already_imported && <span className="text-[10px] text-green-500 shrink-0">✓ importado</span>}
                  </label>
                ))}
              </div>
            </>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setImportOpen(null)}>Fechar</Button>
            {pulled.length > 0 && (
              <Button onClick={importSelected} disabled={importing || !importPlanId}>
                {importing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Importar selecionados ({pulled.filter(c=>selected[c.panel_customer_id] && !c.already_imported).length})
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de planos do Gerador Pro */}
      <Dialog open={!!plansOpen} onOpenChange={(o) => { if (!o) setPlansOpen(null); }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ImageIcon className="w-5 h-5 text-primary" /> Planos do {plansOpen?.name || 'Gerador Pro'}
            </DialogTitle>
            <DialogDescription>
              Crie planos por duração (meses). Use estes planos nos clientes que importar do Gerador Pro — a renovação usa a duração do plano.
            </DialogDescription>
          </DialogHeader>

          {/* Criar novo */}
          <div className="rounded-xl border border-primary/25 bg-primary/[0.04] p-3 space-y-2">
            <p className="text-xs font-semibold text-primary">Novo plano</p>
            <Input placeholder="Nome (ex.: Mensal, Trimestral)" value={newPlan.name}
              onChange={(e) => setNewPlan({ ...newPlan, name: e.target.value })} />
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Duração (meses)</Label>
                <Select value={String(newPlan.months)} onValueChange={(v) => setNewPlan({ ...newPlan, months: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[1,2,3,6,12].map((m) => <SelectItem key={m} value={String(m)}>{m} {m===1?'mês':'meses'}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Preço (R$)</Label>
                <Input type="number" placeholder="30.00" value={newPlan.price}
                  onChange={(e) => setNewPlan({ ...newPlan, price: e.target.value })} />
              </div>
            </div>
            <Button className="w-full" size="sm" onClick={createPlan}>+ Criar plano</Button>
          </div>

          {/* Lista */}
          <div className="mt-3 space-y-2">
            <p className="text-xs text-muted-foreground">Planos existentes</p>
            {plans.length === 0 && <p className="text-xs text-muted-foreground text-center py-3">Nenhum plano ainda.</p>}
            {plans.map((p) => (
              <div key={p.id} className="flex items-center justify-between rounded-lg border border-border/60 px-3 py-2 gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{p.name}</p>
                  <p className="text-xs text-muted-foreground">{p.duration_months} {p.duration_months===1?'mês':'meses'} · R$ {Number(p.price).toFixed(2)}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <label className="flex items-center gap-1.5 text-[11px] text-muted-foreground cursor-pointer" title="Exibir este plano no link de pagamento">
                    <Switch checked={!!p.show_in_payment} onCheckedChange={(v) => toggleShowInPayment(p, v)} />
                    <span className="hidden sm:inline">No link</span>
                  </label>
                  <Button size="icon" variant="ghost" className="text-red-500" onClick={() => deletePlan(p.id)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPlansOpen(null)}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal conectar */}
      <Dialog open={!!connectOpen} onOpenChange={(o) => { if (!o) setConnectOpen(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="text-xl">{connectOpen?.icon}</span> Conectar {connectOpen?.name}
            </DialogTitle>
            <DialogDescription>
              Entre com o usuário e senha da sua conta {connectOpen?.name}. A senha é guardada criptografada.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Apelido (opcional)</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={connectOpen?.name} />
            </div>
            <div>
              <Label className="text-xs">{connectOpen?.loginEmail ? 'Email' : 'Usuário'}</Label>
              <Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} placeholder={connectOpen?.loginEmail ? 'seu@email.com' : 'Seu usuário'} />
            </div>
            <div>
              <Label className="text-xs">Senha</Label>
              <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Sua senha" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConnectOpen(null)}>Cancelar</Button>
            <Button onClick={doConnect} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Conectar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
